import React, { useState } from 'react';
import { supabase } from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const Auth = ({ onBack }) => {
  const [view, setView] = useState('login'); // 'login', 'signup', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (view === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (view === 'signup') {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        if (data?.user?.identities?.length === 0) {
          setError("This email is already registered. Please switch to log in.");
          setView('login');
          return;
        }
        setSuccessMsg("Success! Your account has been created. You can now log in.");
        setView('login');
      } else if (view === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin,
        });
        if (error) throw error;
        setSuccessMsg("Check your email! We've sent you a password reset link.");
        setView('login');
      }
    } catch (err) {
      if (err.message === 'Failed to fetch') {
        setError("Network Error: Failed to reach the database. Please check your connection.");
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (view === 'login') return 'Welcome Back';
    if (view === 'signup') return 'Create Account';
    return 'Reset Password';
  };

  const getDescription = () => {
    if (view === 'login') return 'Sign in to save your progress';
    if (view === 'signup') return 'Join to build your strategy roadmap';
    return "Enter your email and we'll send a reset link";
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${view === 'signup' ? 'bg-blue-600' : 'bg-slate-900'} flex items-center justify-center p-4`}>
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white rounded-[40px] shadow-2xl overflow-hidden relative"
      >
        <button 
          onClick={onBack} 
          className="absolute top-8 left-8 text-slate-400 hover:text-slate-600 transition-colors z-10"
        >
          <SafeIcon name="ArrowLeft" className="w-6 h-6" />
        </button>

        <div className="p-10 pt-16">
          <div className="flex items-center gap-3 mb-10 justify-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-xl shadow-blue-100">
              <SafeIcon name="Compass" className="w-8 h-8" />
            </div>
          </div>

          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
              {getTitle()}
            </h2>
            <p className="text-slate-500 text-sm font-medium mt-2">
              {getDescription()}
            </p>
          </div>

          <form onSubmit={handleAuth} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Email Address</label>
              <input 
                type="email" 
                required 
                placeholder="name@company.com" 
                className="w-full px-6 py-4.5 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 font-medium"
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
              />
            </div>

            {view !== 'forgot' && (
              <div>
                <div className="flex justify-between items-center mb-2 ml-1">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Password</label>
                  {view === 'login' && (
                    <button 
                      type="button"
                      onClick={() => setView('forgot')}
                      className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:text-blue-700"
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <input 
                  type="password" 
                  required 
                  placeholder="••••••••" 
                  className="w-full px-6 py-4.5 rounded-2xl border border-slate-200 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all placeholder:text-slate-300 font-medium"
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                />
              </div>
            )}

            <AnimatePresence mode="wait">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-red-50 text-red-600 p-5 rounded-2xl text-xs font-bold flex items-start gap-3 border border-red-100"
                >
                  <SafeIcon name="AlertCircle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{error}</span>
                </motion.div>
              )}

              {successMsg && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-emerald-50 text-emerald-600 p-5 rounded-2xl text-xs font-bold flex items-start gap-3 border border-emerald-100"
                >
                  <SafeIcon name="CheckCircle" className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{successMsg}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              disabled={loading} 
              className="w-full bg-slate-900 text-white font-black text-xs uppercase tracking-[0.2em] py-5 rounded-2xl hover:bg-blue-600 transition-all shadow-xl shadow-slate-200 active:scale-[0.98] disabled:opacity-50 mt-4"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </div>
              ) : (
                view === 'login' ? 'Sign In' : view === 'signup' ? 'Create Account' : 'Send Reset Link'
              )}
            </button>
          </form>

          <div className="mt-10 text-center">
            {view === 'forgot' ? (
              <button 
                onClick={() => setView('login')} 
                className="text-xs font-black text-blue-600 uppercase tracking-widest hover:text-blue-700 transition-colors"
              >
                Back to Login
              </button>
            ) : (
              <button 
                onClick={() => setView(view === 'login' ? 'signup' : 'login')} 
                className="text-xs font-black text-blue-600 uppercase tracking-widest hover:text-blue-700 transition-colors"
              >
                {view === 'login' ? "Don't have an account? Sign up" : "Already have an account? Log in"}
              </button>
            )}
          </div>
        </div>

        <div className="bg-slate-50 border-t border-slate-100 p-8 text-center">
          <button 
            onClick={onBack} 
            className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] hover:text-slate-600 transition-colors"
          >
            Continue Filling Form as Guest
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Auth;